import { Controller } from "@hotwired/stimulus"

// accounts/verify_email.haml  data-controller="accounts--verify-email"
export default class extends Controller {
  static targets = [
    "digit",
    "form",
    "processing",
    "notification",
    "error",
    "resendLink",
    "resendWrapper",
    "timer",
    "closeNotification",
  ]

  static values = {
    captchaSiteKey: String,
    captchaLocale: String,
  }

  connect() {
    this.intervalId = null
    this.previousHandleVerifyEmailFormSubmit = window.handleVerifyEmailFormSubmit
    this.previousOnRecaptchaElementLoad = window.onRecaptchaElementLoad
    this.handleVerifyEmailFormSubmit = () => this.submit()
    this.handleRecaptchaLoad = () => this.onRecaptchaLoad()
    window.handleVerifyEmailFormSubmit = this.handleVerifyEmailFormSubmit
    window.onRecaptchaElementLoad = this.handleRecaptchaLoad

    this.countdownResendCode(parseInt(this.timerTarget.dataset.secondsTimer, 10) || 0)
    this.disableVerifyButton()
    this.loadRecaptchaScript()
  }

  disconnect() {
    clearInterval(this.intervalId)
    this.intervalId = null

    if (window.handleVerifyEmailFormSubmit === this.handleVerifyEmailFormSubmit) {
      window.handleVerifyEmailFormSubmit = this.previousHandleVerifyEmailFormSubmit
    }

    if (window.onRecaptchaElementLoad === this.handleRecaptchaLoad) {
      window.onRecaptchaElementLoad = this.previousOnRecaptchaElementLoad
    }
  }

  focusDigit(event) {
    const firstEmpty = this.digitTargets.find((field) => field.value.length === 0)
    const filledFields = this.digitTargets.filter((field) => field.value.length > 0)
    const lastFilled = filledFields[filledFields.length - 1]

    if (firstEmpty && firstEmpty !== event.currentTarget) {
      firstEmpty.focus()
    } else if (!firstEmpty && lastFilled) {
      lastFilled.focus()
    }
  }

  digitInput(event) {
    this.hideErrorMessage()
    const input = event.currentTarget

    if (input.value.length === input.maxLength && this.isNumber(input.value)) {
      this.focusNextEmptyField(input)
    } else {
      input.value = ""
    }

    this.checkAllDigitsFilled()
  }

  digitKeydown(event) {
    const input = event.currentTarget
    if (event.key !== "Backspace" || input.value.length !== 0) return

    event.preventDefault()
    const previousField = this.focusPreviousField(input)
    if (previousField) {
      previousField.value = ""
      previousField.focus()
      this.checkAllDigitsFilled()
    }
  }

  digitPaste(event) {
    event.preventDefault()
    this.hideErrorMessage()

    const pasteData = (event.clipboardData || window.clipboardData).getData("text")
    if (pasteData.length === 6 && /^\d{6}$/.test(pasteData)) {
      pasteData.split("").forEach((digit, index) => {
        const field = this.digitTargets[index]
        if (field) {
          field.value = digit
          field.focus()
        }
      })
    }

    this.checkAllDigitsFilled()
  }

  closeNotification() {
    this.hideNotificationMessage()
  }

  resendCode(event) {
    event.preventDefault()
    this.hideErrorMessage()
    this.hideNotificationMessage()
    this.hideProcessingIndicator()
    this.disableVerifyButton()
    this.clearFields()

    fetch(this.resendLinkTarget.href, {
      method: "POST",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        "Accept": "application/json",
      },
      mode: "cors",
      cache: "no-cache",
      credentials: "same-origin",
      referrerPolicy: "no-referrer",
    })
      .then((response) => response.json())
      .then((data) => {
        this.hideProcessingIndicator()
        if (data.type === "success") {
          this.showNotificationMessage()
          this.countdownResendCode(data.data.seconds_timer)
        } else if (data.code === "code_not_expired") {
          console.warn("Code not expired yet:", data.message)
        } else {
          this.showErrorMessage()
        }
      })
      .catch(() => {
        this.hideProcessingIndicator()
        this.showErrorMessage()
      })
  }

  submit() {
    if (this.validateCode()) {
      this.hideNotificationMessage()
      this.showProcessingIndicator()
      this.sendVerifyEmailRequest()
    }
  }

  onRecaptchaLoad() {
    if (window.grecaptcha && this.verifyButton && !this.verifyButton.querySelector("iframe")) {
      window.grecaptcha.render(this.verifyButton.id, {
        sitekey: this.captchaSiteKeyValue,
        hl: this.captchaLocaleValue,
        theme: "dark",
      })
    }
    this.disableVerifyButton()
  }

  loadRecaptchaScript() {
    if (window.grecaptcha) {
      this.onRecaptchaLoad()
      return
    }

    if (document.getElementById("recaptcha-api-script")) return

    const script = document.createElement("script")
    script.id = "recaptcha-api-script"
    script.src = "https://www.google.com/recaptcha/api.js?onload=onRecaptchaElementLoad&render=explicit"
    script.async = true
    script.defer = true
    document.head.appendChild(script)
  }

  focusNextEmptyField(currentInput) {
    const currentIndex = this.digitTargets.indexOf(currentInput)
    for (let i = currentIndex + 1; i < this.digitTargets.length; i++) {
      if (this.digitTargets[i].value.length === 0) {
        this.digitTargets[i].focus()
        return
      }
    }
  }

  focusPreviousField(currentInput) {
    const currentIndex = this.digitTargets.indexOf(currentInput)
    if (currentIndex > 0) {
      this.digitTargets[currentIndex - 1].focus()
      return this.digitTargets[currentIndex - 1]
    }
    return null
  }

  countdownResendCode(seconds) {
    clearInterval(this.intervalId)
    this.resendLinkTarget.classList.add("disabled")
    this.resendWrapperTarget.classList.add("disabled")

    let remainingSeconds = seconds
    this.intervalId = setInterval(() => {
      if (remainingSeconds <= 0) {
        clearInterval(this.intervalId)
        this.intervalId = null
        this.resendLinkTarget.classList.remove("disabled")
        this.resendWrapperTarget.classList.remove("disabled")
        this.timerTarget.textContent = ""
      } else {
        const mins = Math.floor(remainingSeconds / 60)
        const secs = remainingSeconds % 60
        this.timerTarget.textContent = ` ${mins}:${secs.toString().padStart(2, "0")}`
        remainingSeconds--
      }
    }, 1000)
  }

  showProcessingIndicator() {
    this.processingTarget.classList.remove("hidden_element")
  }

  hideProcessingIndicator() {
    this.processingTarget.classList.add("hidden_element")
  }

  showNotificationMessage() {
    this.notificationTarget.classList.remove("hidden_element")
  }

  hideNotificationMessage() {
    this.notificationTarget.classList.add("hidden_element")
  }

  hideErrorMessage() {
    this.errorTarget.classList.add("hidden_element")
    this.digitTargets.forEach((input) => input.classList.remove("invalid"))
  }

  showErrorMessage() {
    this.errorTarget.classList.remove("hidden_element")
    this.digitTargets.forEach((input) => input.classList.add("invalid"))
    this.disableVerifyButton()
  }

  validateCode() {
    return this.digitTargets.every((input) => input.value.length > 0)
  }

  isNumber(str) {
    return /^\d$/.test(str)
  }

  sendVerifyEmailRequest() {
    fetch(this.formTarget.action, {
      method: "POST",
      body: new FormData(this.formTarget),
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        "Accept": "application/json",
      },
      mode: "cors",
      cache: "no-cache",
      credentials: "same-origin",
      referrerPolicy: "no-referrer",
    })
      .then((response) => response.json())
      .then((data) => {
        this.hideProcessingIndicator()
        if (data.code === "already_verified" || data.code === "verified") {
          window.location.href = data.data.redirect_url
        } else {
          this.showErrorMessage()
        }
      })
      .catch(() => {
        this.hideProcessingIndicator()
        this.showErrorMessage()
      })
  }

  checkAllDigitsFilled() {
    if (this.digitTargets.every((input) => input.value.length > 0)) {
      this.enableVerifyButton()
    } else {
      this.disableVerifyButton()
    }
  }

  clearFields() {
    this.digitTargets.forEach((input) => {
      input.value = ""
    })
  }

  disableVerifyButton() {
    if (!this.verifyButton) return
    this.verifyButton.disabled = true
    this.verifyButton.classList.remove("enabled")
  }

  enableVerifyButton() {
    if (!this.verifyButton) return
    this.verifyButton.disabled = false
    this.verifyButton.classList.add("enabled")
  }

  get verifyButton() {
    return this.element.querySelector("#verify-email-submit")
  }
}
